import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { portfolioItems } from "../data/portfolio";
import PlanetPreview from "../components/PlanetPreview";
import "./ProjectPage.css";

/* ── Helpers ── */
function clamp(min, value, max) {
  return Math.min(max, Math.max(min, value));
}

function adjustColor(hex, amount) {
  const num = parseInt(hex.replace("#", ""), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amount));
  const b = Math.min(255, Math.max(0, (num & 0x0000ff) + amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

function getContrastColor(hex) {
  const num = parseInt(hex.replace("#", ""), 16);
  const r = num >> 16;
  const g = (num >> 8) & 0x00ff;
  const b = num & 0x0000ff;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? "#2a2a2a" : "#ffffff";
}

function hexToRgb(hex) {
  if (!hex) return null;
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!match) return null;
  return {
    r: parseInt(match[1], 16),
    g: parseInt(match[2], 16),
    b: parseInt(match[3], 16),
  };
}

function rgba(hex, alpha) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

/* ── Constants ── */
const TRANSITION_MS = 800;

export default function ProjectPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const project = useMemo(
    () => portfolioItems.find((p) => p.id === id) || null,
    [id],
  );

  const scrollRef = useRef(null);
  const sectionRefs = useRef([]);
  const momentumRef = useRef(null);
  const snapTimerRef = useRef(null);
  const dragRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startScrollLeft: 0,
    lastX: 0,
    lastTime: 0,
    velocity: 0,
    samples: [],
  });

  const [activeSection, setActiveSection] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const [contentReady, setContentReady] = useState(false);
  const [heroHovered, setHeroHovered] = useState(false);

  const transitionData = location.state?.transition || {};

  /* ── Derived colours ── */
  const bgColor = project?.color || project?.image || "#04050f";
  const textColor = getContrastColor(bgColor);
  const accentColor = project?.text || textColor;
  const lighterBg = adjustColor(bgColor, 14);

  const planetKey = project?.id || "mercury";

  /* ── Build sections ── */
  const sections = useMemo(() => {
    if (!project) return [];
    const items = [{ type: "hero" }]; // split-panel hero+info
    const images = (project.images || []).filter(Boolean);
    images.forEach((url, i) =>
      items.push({ type: "image", url, index: i, total: images.length }),
    );
    return items;
  }, [project]);

  const totalSections = sections.length;

  /* ── Reveal entry ── */
  useEffect(() => {
    if (!project) return;
    const timer = setTimeout(() => setContentReady(true), 100);
    return () => clearTimeout(timer);
  }, [project]);

  /* ── Lock body scroll ── */
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  /* ── Redirect if missing project ── */
  useEffect(() => {
    if (!project) navigate("/", { replace: true });
  }, [navigate, project]);

  /* ── Cleanup ── */
  useEffect(() => {
    return () => {
      if (momentumRef.current) cancelAnimationFrame(momentumRef.current);
      if (snapTimerRef.current) clearTimeout(snapTimerRef.current);
    };
  }, []);

  /* ── Active section tracking ── */
  const updateActiveSection = useCallback(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const center = scroller.scrollLeft + scroller.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    sectionRefs.current.forEach((el, i) => {
      if (!el) return;
      const elCenter = el.offsetLeft + el.offsetWidth / 2;
      const dist = Math.abs(elCenter - center);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    setActiveSection(best);
  }, []);

  /* ── Scroll event ── */
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        updateActiveSection();
      });
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [updateActiveSection]);

  /* ── Wheel → horizontal ── */
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;

    let wheelTarget = scroller.scrollLeft;
    let wheelRaf = null;

    const smoothWheel = () => {
      const diff = wheelTarget - scroller.scrollLeft;
      if (Math.abs(diff) < 0.5) {
        wheelRaf = null;
        return;
      }
      const ease =
        Math.abs(diff) > 200 ? 0.16 : Math.abs(diff) > 50 ? 0.1 : 0.07;
      scroller.scrollLeft += diff * ease;
      wheelRaf = requestAnimationFrame(smoothWheel);
    };

    const onWheel = (e) => {
      if (isExiting) return;
      if (e.deltaX === 0 && e.deltaY === 0) return;
      e.preventDefault();
      cancelMomentum();
      const delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
      wheelTarget = scroller.scrollLeft + delta * 1.8;
      if (!wheelRaf) wheelRaf = requestAnimationFrame(smoothWheel);
    };

    scroller.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      scroller.removeEventListener("wheel", onWheel);
      if (wheelRaf) cancelAnimationFrame(wheelRaf);
    };
  }, [isExiting]);

  /* ── Keyboard ── */
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        handleClose();
        return;
      }
      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        e.preventDefault();
        const dir = e.key === "ArrowRight" ? 1 : -1;
        const next = clamp(0, activeSection + dir, sections.length - 1);
        scrollToSection(next);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeSection, sections.length, isExiting]);

  /* ── Scroll to section ── */
  const scrollToSection = useCallback((index) => {
    const el = sectionRefs.current[index];
    if (!el) return;
    el.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "start",
    });
  }, []);

  /* ── Cancel momentum ── */
  const cancelMomentum = useCallback(() => {
    if (momentumRef.current) {
      cancelAnimationFrame(momentumRef.current);
      momentumRef.current = null;
    }
  }, []);

  /* ── Close ── */
  const handleClose = useCallback(() => {
    if (isExiting) return;
    setIsExiting(true);

    cancelMomentum();
    if (snapTimerRef.current) clearTimeout(snapTimerRef.current);

    if (project?.id) {
      window.sessionStorage.setItem("returnProjectId", project.id);
    }

    setTimeout(() => {
      if (location.state?.backgroundLocation) {
        navigate(-1);
        return;
      }
      navigate("/", {
        state: {
          returnFromProject: true,
          returnProjectId: project?.id || null,
        },
      });
    }, TRANSITION_MS);
  }, [cancelMomentum, isExiting, location.state, navigate, project?.id]);

  /* ── Pointer drag ── */
  const handlePointerDown = useCallback(
    (e) => {
      if (isExiting) return;
      if (e.pointerType === "touch") return;
      if (e.button !== 0) return;
      const scroller = scrollRef.current;
      if (!scroller) return;

      cancelMomentum();
      dragRef.current = {
        active: true,
        pointerId: e.pointerId,
        startX: e.clientX,
        startScrollLeft: scroller.scrollLeft,
        lastX: e.clientX,
        lastTime: performance.now(),
        velocity: 0,
        samples: [{ x: e.clientX, t: performance.now() }],
      };
      scroller.setPointerCapture(e.pointerId);
    },
    [cancelMomentum, isExiting],
  );

  const handlePointerMove = useCallback((e) => {
    const scroller = scrollRef.current;
    if (!scroller || !dragRef.current.active) return;
    if (e.pointerId !== dragRef.current.pointerId) return;

    const now = performance.now();
    const dx = e.clientX - dragRef.current.startX;
    scroller.scrollLeft = dragRef.current.startScrollLeft - dx;

    const sample = { x: e.clientX, t: now };
    dragRef.current.samples.push(sample);
    dragRef.current.samples = dragRef.current.samples.filter(
      (s) => s.t >= now - 100,
    );
    const dt = now - dragRef.current.lastTime;
    if (dt > 0) {
      dragRef.current.velocity = (e.clientX - dragRef.current.lastX) / dt;
    }
    dragRef.current.lastX = e.clientX;
    dragRef.current.lastTime = now;
  }, []);

  const handlePointerUp = useCallback(
    (e) => {
      const scroller = scrollRef.current;
      if (!scroller || !dragRef.current.active) return;
      if (e.pointerId !== dragRef.current.pointerId) return;
      dragRef.current.active = false;

      try {
        scroller.releasePointerCapture(e.pointerId);
      } catch {}

      if (isExiting) return;

      const samples = dragRef.current.samples;
      let avgVelocity = dragRef.current.velocity;
      if (samples.length >= 2) {
        const first = samples[0];
        const last = samples[samples.length - 1];
        const dt = last.t - first.t;
        if (dt > 4) avgVelocity = (last.x - first.x) / dt;
      }

      const velocityX = -avgVelocity * 30;
      if (Math.abs(velocityX) < 0.8) return;

      let current = velocityX;
      const frictionHigh = 0.965;
      const frictionLow = 0.992;
      const frictionKnee = 6;
      const min = 0.15;

      const step = () => {
        if (Math.abs(current) < min) {
          momentumRef.current = null;
          return;
        }
        scroller.scrollLeft += current;
        const speed = Math.abs(current);
        const t = Math.min(speed / frictionKnee, 1);
        const friction = frictionLow + (frictionHigh - frictionLow) * t;
        current *= friction;
        momentumRef.current = requestAnimationFrame(step);
      };
      momentumRef.current = requestAnimationFrame(step);
    },
    [isExiting],
  );

  /* ── Resize ── */
  useEffect(() => {
    const handleResize = () => updateActiveSection();
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, [updateActiveSection]);

  if (!project) return null;

  const progress = totalSections > 1 ? activeSection / (totalSections - 1) : 0;

  return (
    <div
      className={`pp ${isExiting ? "pp--exiting" : ""} ${contentReady ? "pp--ready" : ""}`}
      style={{
        "--pp-bg": bgColor,
        "--pp-bg-light": lighterBg,
        "--pp-text": textColor,
        "--pp-accent": accentColor,
        "--pp-accent-glow": rgba(accentColor, 0.08) || "rgba(255,140,66,0.06)",
        "--pp-accent-glow-strong":
          rgba(accentColor, 0.14) || "rgba(255,140,66,0.10)",
        "--pp-bg-glow": rgba(bgColor, 0.07) || "rgba(180,215,255,0.04)",
      }}
    >
      {/* Background */}
      <div className="pp__bg" aria-hidden="true" />

      {/* Close button */}
      <button
        className="pp__close"
        onClick={handleClose}
        disabled={isExiting}
        aria-label="Close project"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path
            d="M1 1L17 17M17 1L1 17"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {/* Navigation header */}
      <header className="pp__header">
        <span className="pp__header-back" onClick={handleClose}>
          Planet Archive
        </span>
        <span className="pp__header-counter">
          {String(activeSection + 1).padStart(2, "0")} /{" "}
          {String(totalSections).padStart(2, "0")}
        </span>
      </header>

      {/* Horizontal scroller */}
      <div
        className="pp__scroller"
        ref={scrollRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onPointerLeave={handlePointerUp}
      >
        {sections.map((section, i) => {
          if (section.type === "hero") {
            return (
              <section
                key="hero"
                className="pp__section pp__section--hero"
                ref={(el) => (sectionRefs.current[i] = el)}
              >
                {/* Left: Planet */}
                <div className="pp__hero-left">
                  <div
                    className="pp__planet-wrap"
                    onPointerEnter={() => setHeroHovered(true)}
                    onPointerLeave={() => setHeroHovered(false)}
                  >
                    <PlanetPreview
                      planet={planetKey}
                      interactiveEnabled={heroHovered && !isExiting}
                      motionEnabled={!isExiting}
                    />
                  </div>
                </div>

                {/* Right: Info */}
                <div className="pp__hero-right">
                  <div className="pp__info">
                    <h1 className="pp__title">{project.title}</h1>
                    <p className="pp__subtitle">{project.subtitle}</p>
                    <p className="pp__description">{project.description}</p>

                    <div className="pp__divider" />

                    <div className="pp__meta">
                      {project.role && (
                        <div className="pp__meta-item">
                          <span className="pp__meta-label">Role</span>
                          <span className="pp__meta-value">{project.role}</span>
                        </div>
                      )}
                      {project.client && (
                        <div className="pp__meta-item">
                          <span className="pp__meta-label">Client</span>
                          <span className="pp__meta-value">
                            {project.client}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            );
          }

          /* Image section */
          return (
            <section
              key={`img-${section.index}`}
              className="pp__section pp__section--image"
              ref={(el) => (sectionRefs.current[i] = el)}
            >
              <div className="pp__image-frame">
                <img
                  src={section.url}
                  alt={`${project.title} ${section.index + 1}`}
                  className="pp__image"
                  loading="lazy"
                  decoding="async"
                  draggable="false"
                />
              </div>
              <div className="pp__image-caption">
                <span className="pp__image-number">
                  {String(section.index + 1).padStart(2, "0")}
                </span>
                <span className="pp__image-total">
                  / {String(section.total).padStart(2, "0")}
                </span>
              </div>
            </section>
          );
        })}

        {/* End spacer for last image to center */}
        <div className="pp__end-spacer" />
      </div>

      {/* Scroll indicator */}
      <div className="pp__indicator">
        <span>SCROLL TO SEE MORE</span>
        <svg width="24" height="8" viewBox="0 0 24 8" fill="none">
          <path
            d="M0 4H22M22 4L18 1M22 4L18 7"
            stroke="currentColor"
            strokeWidth="1"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Dot navigation */}
      <nav className="pp__dots" aria-label="Section navigation">
        {sections.map((_, i) => (
          <button
            key={i}
            className={`pp__dot ${i === activeSection ? "pp__dot--active" : ""}`}
            onClick={() => scrollToSection(i)}
            aria-label={`Go to section ${i + 1}`}
          />
        ))}
      </nav>

      {/* Progress bar */}
      <div className="pp__progress">
        <div
          className="pp__progress-bar"
          style={{ width: `${progress * 100}%` }}
        />
      </div>
    </div>
  );
}
